import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FileText, Download } from "lucide-react";

const METHODS = ["Cash", "UPI", "Credit Card", "Debit Card", "Bank Transfer"];

export default function Payments() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", consultation_fee: 0, discount: 0, gst_percent: 18, paid_amount: 0, payment_method: "Cash", notes: "" });
  const [viewing, setViewing] = useState(null);

  const load = async () => { const { data } = await api.get("/payments"); setItems(data); };
  useEffect(() => { load(); api.get("/clients").then(r => setClients(r.data)); }, []);

  const create = async () => {
    if (!form.client_id) return toast.error("Select client");
    await api.post("/payments", form);
    toast.success("Invoice created");
    setOpen(false); load();
  };

  const preview = { subtotal: form.consultation_fee - form.discount, gst: ((form.consultation_fee - form.discount) * form.gst_percent) / 100 };
  const total = preview.subtotal + preview.gst;

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Billing</div>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1">Payments & Invoices</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#1E3A8A] btn-pill h-11 px-5" data-testid="new-invoice-btn"><Plus className="w-4 h-4 mr-1.5" /> New invoice</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">Generate invoice</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Client</Label>
                <Select value={form.client_id} onValueChange={v=>setForm({...form,client_id:v})}>
                  <SelectTrigger><SelectValue placeholder="Choose client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Field label="Consultation fee (₹)"><Input type="number" value={form.consultation_fee} onChange={e=>setForm({...form,consultation_fee:parseFloat(e.target.value||0)})} /></Field>
              <Field label="Discount (₹)"><Input type="number" value={form.discount} onChange={e=>setForm({...form,discount:parseFloat(e.target.value||0)})} /></Field>
              <Field label="GST %"><Input type="number" value={form.gst_percent} onChange={e=>setForm({...form,gst_percent:parseFloat(e.target.value||0)})} /></Field>
              <Field label="Paid amount (₹)"><Input type="number" value={form.paid_amount} onChange={e=>setForm({...form,paid_amount:parseFloat(e.target.value||0)})} /></Field>
              <Field label="Payment method">
                <Select value={form.payment_method} onValueChange={v=>setForm({...form,payment_method:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="col-span-2 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-xl text-sm font-mono">
                <div className="flex justify-between"><span>Subtotal</span><span>₹{preview.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>GST</span><span>₹{preview.gst.toFixed(2)}</span></div>
                <div className="flex justify-between text-amber-600 font-semibold mt-1 pt-1 border-t border-slate-200 dark:border-slate-700"><span>Total</span><span>₹{total.toFixed(2)}</span></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-[#1E3A8A] btn-pill" data-testid="submit-invoice">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <Card className="p-12 rounded-2xl text-center border-dashed text-slate-500">No invoices yet.</Card>}
        {items.map(p => (
          <Card key={p.id} className="p-5 rounded-2xl card-lift flex flex-col md:flex-row md:items-center gap-4 justify-between" data-testid={`invoice-${p.id}`}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 flex items-center justify-center"><FileText className="w-5 h-5" strokeWidth={1.5} /></div>
              <div>
                <div className="font-mono text-amber-600">{p.invoice_number}</div>
                <div className="font-medium">{p.client_name}</div>
                <div className="text-xs text-slate-500">{p.payment_method} • {(p.created_at || "").slice(0,10)}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="font-display text-2xl">₹{p.total?.toLocaleString()}</div>
                <div className="text-xs text-slate-500">Paid ₹{p.paid_amount?.toLocaleString()} • Pending ₹{p.pending_amount?.toLocaleString()}</div>
              </div>
              <Badge className={
                p.status === "paid" ? "bg-emerald-100 text-emerald-800" :
                p.status === "partial" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"
              }>{p.status}</Badge>
              <Button variant="outline" size="sm" onClick={() => setViewing(p)} data-testid={`view-invoice-${p.id}`}>View</Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="max-w-2xl" data-testid="invoice-preview">
          {viewing && <InvoicePreview payment={viewing} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>);

function InvoicePreview({ payment }) {
  const printInvoice = () => window.print();
  return (
    <div className="p-2">
      <div className="border border-amber-300 rounded-2xl p-8">
        <div className="flex justify-between items-start">
          <div>
            <div className="font-display text-2xl text-[#1E3A8A]">Astrologer Trinandan Mishra</div>
            <div className="text-xs text-slate-500 mt-1">Vedic Wisdom · Vastu · Numerology · Gemstones</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Invoice</div>
            <div className="font-mono text-amber-600 text-lg">{payment.invoice_number}</div>
            <div className="text-xs text-slate-500">{(payment.created_at || "").slice(0,10)}</div>
            <div className="text-[10px] text-slate-500 mt-1" data-testid="invoice-printed-at">
              Printed: {new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} IST
            </div>
          </div>
        </div>
        <div className="mt-6 gold-divider" />
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Billed to</div>
            <div className="font-medium">{payment.client_name}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Payment Method</div>
            <div className="font-medium">{payment.payment_method}</div>
          </div>
        </div>
        <table className="w-full mt-6 text-sm">
          <tbody>
            <tr className="border-b"><td className="py-2">Consultation fee</td><td className="text-right">₹{payment.consultation_fee?.toFixed(2)}</td></tr>
            <tr className="border-b"><td className="py-2">Products</td><td className="text-right">₹{payment.products_total?.toFixed(2)}</td></tr>
            <tr className="border-b"><td className="py-2">Discount</td><td className="text-right">-₹{payment.discount?.toFixed(2)}</td></tr>
            <tr className="border-b"><td className="py-2">Subtotal</td><td className="text-right">₹{payment.subtotal?.toFixed(2)}</td></tr>
            <tr className="border-b"><td className="py-2">GST ({payment.gst_percent}%)</td><td className="text-right">₹{payment.gst_amount?.toFixed(2)}</td></tr>
            <tr className="font-semibold text-amber-600"><td className="py-3 font-display text-lg">Total</td><td className="text-right font-display text-lg">₹{payment.total?.toFixed(2)}</td></tr>
          </tbody>
        </table>
        <div className="mt-8 text-center text-xs text-slate-500">Om Ganeshaya Namah · Thank you for your visit.</div>
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={printInvoice} className="btn-pill"><Download className="w-4 h-4 mr-1.5" /> Print / Save PDF</Button>
      </div>
    </div>
  );
}
